<script>
import { mapGetters } from 'vuex'
import isEmpty from 'lodash.isempty'
import ClickOutside from 'vue-click-outside'

export default {
    name: 'DeliveryDetails',
    directives: {
        ClickOutside,
    },
    props: {
        sticky: Boolean,
        header: Boolean,
        stretch: Boolean,
    },
    data() {
        return {
            showTooltip: false,
        }
    },
    computed: {
        ...mapGetters({
            activeCanteen: 'active/canteen',
            orderAddress: 'order/address',
            orderTime: 'order/time',
            orderAsap: 'order/asap',
            isXs: 'isXs',
        }),
        time() {
            return isEmpty(this.activeCanteen) ? this.orderTime : this.activeCanteen.delivery_time
        },
        asap() {
            return isEmpty(this.activeCanteen) ? this.orderAsap : false
        },
        today() {
            return this.time ? this.$moment().isSame(this.time, 'day') : false
        },
        tomorrow() {
            return this.time ? this.$moment().add(1, 'day').isSame(this.time, 'day') : false
        },
        hasAddress() {
            return !isEmpty(this.orderAddress) || !isEmpty(this.activeCanteen)
        },
        blockDeliveryTimeSelection() {
            return Boolean(!isEmpty(this.activeCanteen) || this.$route.meta.blockDeliveryTime)
        },
        blockLocationSelection() {
            return Boolean(!isEmpty(this.activeCanteen) || this.$route.meta.blockLocation)
        },
        showDeliveryDetails() {
            return !this.$route.meta.auth && !this.$route.meta.scope?.canteenSelector
        },
        addresses() {
            return this.$addresses
        },
        deliveryAddress() {
            return !isEmpty(this.activeCanteen)
                ? this.activeCanteen.isResidential
                    ? this.activeCanteen.address
                    : this.$addresses.findItem(this.getId(this.activeCanteen.address))
                : this.orderAddress
        },
    },
    watch: {
        '$route.name': {
            handler(name, nameBefore) {
                if (name !== nameBefore && !this.hasAddress && !name.includes('loader'))
                    this.showTooltip = true
            },
        },
    },
    created() {
        this.showTooltip = !this.hasAddress
    },
    methods: {
        setTime() {
            if (!this.blockDeliveryTimeSelection) this.$time.open()
        },
        setAddress() {
            if (!this.blockLocationSelection) {
                if (this.showTooltip) this.showTooltip = false
                if (this.$user?.isLoggedIn && !!this.$addresses?.total) this.$location.select.open()
                else this.$location.enter.open()
            }
        },
        hideTooltip() {
            if (!this.hasAddress) this.showTooltip = false
        },
    },
}
</script>

<template lang="pug">
transition(name="fade")
    .delivery-details.d-flex.align-items-center.gap-5(v-if="showDeliveryDetails" v-click-outside="hideTooltip" :class="{'sticky': sticky, 'in-header': header, 'full-width': stretch}")
        .btn.btn-mono.btn-xs.btn-pill.min-w-0(
            :role="blockDeliveryTimeSelection ? null : 'button'"
            @click="setTime()"
            aria-label="delivery time"
            :class="{disabled: blockDeliveryTimeSelection}")
            icon.mr-3(v-if="!isXs" icon="fa-solid fa-clock" size="lg")
            transition(name="fade" mode="out-in")
                span.text-ellipsis.font-weight-semibold
                    template(v-if="asap") {{ $t('details.now') }}
                    template(v-else)  {{ time | toDeliveryTime}}                
            icon.ml-3(v-if="!blockLocationSelection" icon="fa-solid fa-chevron-down" size="lg")
        #missingAddressTooltip.min-w-0(@click="setAddress")
            .btn.btn-mono.btn-xs.btn-pill.w-100(
                :role="blockLocationSelection ? null : 'button'"                        
                :class="[ hasAddress || blockLocationSelection ? 'btn-mono' : 'btn-alert', {disabled: blockLocationSelection}]")                    
                template(v-if="!isXs")
                    transition(name="fade" mode="out-in")
                        icon.mr-3(v-if="hasAddress" icon="fa-solid fa-location-dot" size="lg")
                        icon.mr-3.text-alert(v-else icon="fa-solid fa-exclamation-triangle" size="lg")                        
                transition(name="fade" mode="out-in")
                    span.text-ellipsis.font-weight-semibold
                        template(v-if="!hasAddress") {{$t('details.no_address')}} 
                        template(v-else-if="deliveryAddress.name") {{deliveryAddress.name}}
                        template(v-else) {{deliveryAddress.full_address}}
                icon.ml-3(v-if="!blockLocationSelection" icon="fa-solid fa-chevron-down" size="lg")
        b-tooltip(
            v-if="!hasAddress" 
            :show.sync="showTooltip" 
            target="missingAddressTooltip" 
            container="missingAddressTooltip" 
            :triggers="showTooltip ? 'click' : 'hover focus'"
            custom-class="tooltip-p-0 cursor-pointer" 
            placement="bottomright" 
            variant="info")
            .py-2.px-3(:inner-html.prop="$t('details.address_tooltip')")
</template>
